/**
 * Sale Policy Module
 *
 * Handles the sale-item policy consent modal flow. When a product is on
 * sale and the feature flag is enabled, the customer must accept a
 * disclaimer before the item is added to the cart. A line-item property
 * (`_Sale Terms Accepted`) records the acceptance.
 *
 * Hemming disclaimer takes priority — if an item is hemmed, the sale
 * policy modal is not shown because hemmed items are already ineligible
 * for any return.
 *
 * Cross-module compat shim. The IIFE common-products-controller still
 * calls salePolicy.shouldShowModal / openSalePolicyModal / resetForm
 * during the form-submit flow; this module sets `window.salePolicy`
 * with the same surface so those calls keep resolving. Drop the shim
 * in Phase 13.
 *
 * Migrated from theme/js/modules/sale-policy.js (IIFE → ESM, Phase 11).
 *
 * Source path: theme/js/modules/sale-policy.esm.js
 * Built name : ./sale-policy.js (sibling imports must use the built name)
 *
 * AI: Entirely written by AI (originally 2026-03-28; ESM rewrite 2026-05-05)
 * Date: 2026-05-05
 * Model: Claude Opus 4.7
 *
 * [SalePolicy]
 */

import { log } from './log.js';
import { appGet, safeAppCall } from './app-get.js';
import { push as notifierPush, dismiss as notifierDismiss } from './notifier.js';
import { unwrapJQuery } from './dom.js';
import { toggleFormEnabled } from './form-state.js';
import { fetchSection } from './sections.js';

const flagName = 'ep_sale_item_policy_enable';
const acceptTermsSelector = '.js-accept-sale-policy';
const declineTermsSelector = '.js-decline-sale-policy';
const addItemFormSelector = '.js-add-item-form';
const salePolicyPropertySelector = 'input[name="properties[_Sale Terms Accepted]"]';
const acceptSalePolicyCheckboxSelector = 'input[name="accept_sale_policy_terms"]';

// Tracks which form triggered the modal so handleTermsChoice operates
// on the correct form. Product pages can have multiple .js-add-item-form
// elements (PDP + quick-add cards), so document.querySelector alone
// would grab the first match, which may not be the one the user clicked.
let activeForm = null;

// Per-policy-type promise cache. Storing the in-flight promise (not just
// the resolved HTML) means concurrent open calls during a fetch share the
// same request, and the cache survives the page's lifetime so repeat
// opens are zero-cost. Cleared on error so the next attempt re-fetches.
const modalHtmlCache = new Map();

function loadSalePolicyHtml(policyType) {
  if (modalHtmlCache.has(policyType)) return modalHtmlCache.get(policyType);
  const sectionId = 'sale-policy-modal-' + policyType.replace(/_/g, '-');
  const promise = fetchSection(sectionId, '/').catch((err) => {
    modalHtmlCache.delete(policyType);
    throw err;
  });
  modalHtmlCache.set(policyType, promise);
  return promise;
}

function isFlagEnabled() {
  // app.flags(name) prefixes 'flags__' if missing and reads
  // `app.get('flags.flags__name')`. Pre-hydration safe via appGet.
  const key = flagName.indexOf('flags__') < 0 ? 'flags__' + flagName : flagName;
  return appGet('flags.' + key) === true;
}

function ga(args) {
  safeAppCall('ga', 'send', 'event', args);
}

/**
 * Checks whether the sale policy modal should be shown.
 * Accepts the already-serialized form data to avoid a redundant serialize().
 */
export function shouldShowModal($form, serializedData) {
  if (!isFlagEnabled()) return false;
  // $form is a jQuery collection (caller is the IIFE controller). Read
  // both ways so a hypothetical native caller works too.
  const formEl = unwrapJQuery($form);
  if (!formEl || !formEl.querySelector) return false;

  let data = serializedData;
  if (!data) {
    // Native FormData → URL-encoded string, matching jQuery's
    // .serialize() output for the values we test against below.
    const fd = new FormData(formEl);
    data = new URLSearchParams(fd).toString();
  }
  const hasSalePolicyFields = !!formEl.querySelector(acceptSalePolicyCheckboxSelector);
  const hasHem = data.indexOf('properties%5BHem%5D=') !== -1 ||
                 data.indexOf('properties[Hem]=') !== -1;
  const hasAcceptedTerms = data.indexOf('accept_sale_policy_terms=1') !== -1;

  // Hemming disclaimer takes priority — hemmed items are not eligible
  // for any form of return, so sale policy is redundant.
  if (hasHem) return false;
  return hasSalePolicyFields && !hasAcceptedTerms;
}

export async function openSalePolicyModal($form) {
  // Single-modal lock: with multiple .js-add-item-form on a page (quick-add
  // cards), a second call during the first's fetch would overwrite
  // activeForm and route the first modal's accept/decline to the second
  // form — submitting the wrong product. Throwing lets the caller unlock
  // its own form-loading state via .catch.
  if (activeForm) {
    log('[SalePolicy] modal already in flight for another form — refusing');
    throw new Error('[SalePolicy] modal already in flight');
  }
  // Caller passes a jQuery $form (IIFE common-products-controller). Unwrap.
  activeForm = unwrapJQuery($form) || document.querySelector(addItemFormSelector);
  const policyTypeInput = activeForm && activeForm.querySelector('input[name="sale_policy_type"]');
  const policyType = (policyTypeInput && policyTypeInput.value) || 'exchange_or_credit';

  let modalContent;
  try {
    modalContent = await loadSalePolicyHtml(policyType);
  } catch (err) {
    // No silent fallback to a different policy type — showing exchange-
    // or-credit terms when the actual policy is final-sale would record
    // accepted-terms against the wrong disclaimer. Fail closed: clear
    // activeForm and re-throw so the caller's .catch unlocks the form
    // and the customer can retry the ATC click.
    log('[SalePolicy] fetch failed for ' + policyType, err);
    activeForm = null;
    throw err;
  }

  // Defensive: an empty section response means the merchant disabled the
  // flag at the Liquid layer. Don't push a content-less force/disableX
  // modal (would strand the customer). Refuse to open and let the caller
  // unlock the form — the trigger path's isFlagEnabled() check should
  // have stopped us earlier anyway, so this branch indicates a config
  // drift between the JS flag read and the section render.
  if (!modalContent.trim()) {
    log('[SalePolicy] empty section response for ' + policyType + ' — refusing to open');
    activeForm = null;
    throw new Error('[SalePolicy] empty section response');
  }

  notifierPush({
    type: 'modal',
    content: modalContent.trim(),
    name: 'salePolicy',
    force: true,
    disableX: true,
  });
  // Caller (add-item-form _handleSubmit) puts the form into the loading
  // state before invoking us, to block double-submits during the fetch.
  // The modal is now visible and the customer needs to interact — unlock
  // the form so the accept-path requestSubmit can pass _handleSubmit's
  // FORM_LOADING_CLASS guard, and so the decline path's toggleFormEnabled
  // (which already runs) reflects an honest "enabled" state.
  toggleFormEnabled(activeForm, true);
  ga({
    eventCategory: 'FlashMessage',
    eventAction: 'Shown',
    eventLabel: 'Sale policy disclosure',
  });
}

function closeSalePolicyModal() {
  notifierDismiss();
}

function handleTermsChoice(event) {
  const button = event.target.closest(acceptTermsSelector + ', ' + declineTermsSelector);
  if (!button) return;
  const didAccept = button.matches(acceptTermsSelector);
  const formEl = activeForm || document.querySelector(addItemFormSelector);
  if (!formEl) return;
  const checkbox = formEl.querySelector(acceptSalePolicyCheckboxSelector);
  const property = formEl.querySelector(salePolicyPropertySelector);

  // Prevent multiple submissions
  if (formEl.classList.contains('form-loading')) {
    log('Sale policy form already submitting, ignoring duplicate submission');
    return false;
  }

  if (didAccept) {
    closeSalePolicyModal();
    if (checkbox) checkbox.checked = true;
    if (property) property.value = 'Yes';
    // Use a small delay to ensure the modal is fully closed before submitting.
    setTimeout(() => {
      if (typeof formEl.requestSubmit === 'function') formEl.requestSubmit();
      else formEl.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
    }, 100);
    ga({
      eventCategory: 'SalePolicy',
      eventAction: 'Accepted',
      eventLabel: 'Sale policy disclosure accepted',
    });
  } else {
    if (checkbox) checkbox.checked = false;
    if (property) property.value = '';
    closeSalePolicyModal();
    toggleFormEnabled(formEl, true);
    ga({
      eventCategory: 'SalePolicy',
      eventAction: 'Declined',
      eventLabel: 'Sale policy disclosure declined',
    });
  }

  activeForm = null;
}

export function resetForm($form) {
  const formEl = unwrapJQuery($form);
  if (!formEl) return;
  const property = formEl.querySelector(salePolicyPropertySelector);
  const checkbox = formEl.querySelector(acceptSalePolicyCheckboxSelector);
  if (property) property.value = '';
  if (checkbox) checkbox.checked = false;
}

// Document delegate. Only activates handlers when the flag is on, so
// stores without sale-policy enabled don't pay the per-click cost.
document.addEventListener('click', (event) => {
  if (!isFlagEnabled()) return;
  const target = event.target;
  if (!(target instanceof Element)) return;
  if (!target.closest(acceptTermsSelector + ', ' + declineTermsSelector)) return;
  handleTermsChoice(event);
});

log('[SalePolicy] registered');
